import { API_URL } from '../config';

/**
 * Check if the backend is healthy (useful for warming up Cloud Run cold starts)
 * @returns {Promise<{ ok: boolean, status?: number, error?: string }>}
 */
export const checkHealth = async () => {
  if (!API_URL) {
    return { ok: true }; // No API URL configured, assume ready (e.g. local dev)
  }

  try {
    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), 10000); // 10s timeout per request

    const response = await fetch(`${API_URL}/health`, {
      method: 'GET',
      signal: controller.signal,
      credentials: 'omit',
    });

    clearTimeout(timeoutId);

    if (response.ok) {
      const data = await response.json();
      if (data?.status === 'healthy') {
        return { ok: true };
      }
    }
    return { ok: false, status: response.status };
  } catch (error) {
    const message = error.name === 'AbortError' ? 'timeout' : error.message || 'network error';
    return { ok: false, error: message };
  }
};

/**
 * Poll the backend health endpoint until it responds or max attempts are reached.
 * Warms up Cloud Run by triggering the cold start before the user interacts.
 * @param {Object} options
 * @param {number} options.intervalMs - Poll interval in ms
 * @param {number} options.maxAttempts - Max attempts (0 = infinite)
 * @param {number} options.maxWaitMs - Max total wait time in ms (0 = no limit)
 * @param {Function} options.onAttempt - Callback (attempt, result) for each attempt
 * @returns {Promise<boolean>} true if healthy, false if gave up
 */
export const pollHealthUntilReady = async ({
  intervalMs = 2000,
  maxAttempts = 0,
  maxWaitMs = 90000, // 90 seconds default
  onAttempt,
} = {}) => {
  if (!API_URL) {
    return true;
  }

  const start = Date.now();
  let attempt = 0;

  while (true) {
    attempt++;
    const result = await checkHealth();

    if (typeof onAttempt === 'function') {
      onAttempt(attempt, result);
    }

    if (result.ok) {
      return true;
    }

    if (maxAttempts > 0 && attempt >= maxAttempts) {
      return false;
    }

    if (maxWaitMs > 0 && Date.now() - start >= maxWaitMs) {
      return false;
    }

    await new Promise((r) => setTimeout(r, intervalMs));
  }
};
